import {useEffect} from 'react';

// Тримає Screen Wake Lock доки `active === true` — щоб телефон не гасив
// екран під час аплоаду (так само, як це робить YouTube при перегляді відео).
//
// Браузер автоматично відкликає lock коли вкладка йде у фон, тому ми слухаємо
// `visibilitychange` і пере-захоплюємо lock при поверненні. Якщо API недоступний
// (старий браузер / не-secure context) — мовчки no-op, аплоад продовжується.
export function useWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    if (typeof navigator === 'undefined' || !('wakeLock' in navigator)) return;

    let sentinel: WakeLockSentinel | null = null;
    let cancelled = false;

    const acquire = async (): Promise<void> => {
      try {
        const next = await navigator.wakeLock.request('screen');
        if (cancelled) {
          await next.release().catch(() => undefined);
          return;
        }
        sentinel = next;
      } catch {
        // Запит міг не вдатися (no user gesture, battery saver iOS, тощо).
        // Це не критично для основного флоу — пропускаємо.
      }
    };

    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible' && sentinel === null) {
        void acquire();
      }
    };

    void acquire();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      sentinel?.release().catch(() => undefined);
      sentinel = null;
    };
  }, [active]);
}
